import { Color, Euler, FrontSide, Matrix3, Matrix4, ShaderMaterial, Vector2 } from 'three';

import { lisa } from '../../Lisa.js';
import { EVENTS } from '../../constants.js';
import { bindUniform, monitorVector, setShaderProp } from '../../utils/materialsUtils.js';
import { parseConfig } from '../../utils/misc.js';

import fs from './fragment.fs?hotShader';
import vs from './vertex.vs?hotShader';

const _m4 = new Matrix4();

/**
 * @typedef {Object} PBRParams
 * @property {Color} [color] - Base color.
 * @property {number} [metalness] - Metallic factor.
 * @property {number} [roughness] - Roughness factor.
 * @property {THREE.Texture} [map] - Base color texture.
 * @property {THREE.Color} [emissive] - Emissive color.
 * @property {THREE.Texture} [emissiveMap] - Emissive texture.
 * @property {THREE.Color} [ambient] - Ambient color.
 * @property {number} [ambientIntensity] - Ambient intensity.
 * @property {THREE.Texture} [armMap] - Ambient roughness metallic map.
 * @property {THREE.Texture} [aoMap] - Ambient occlusion texture.
 * @property {number} [metalnessMapIntensity] - Metalness map intensity.
 * @property {number} [roughnessMapIntensity] - Roughness map intensity.
 * @property {number} [aoMapIntensity] - Ambient occlusion map intensity.
 * @property {THREE.Texture} [metalnessMap] - Alias for armMap.
 * @property {THREE.Texture} [roughnessMap] - Alias for armMap.
 * @property {THREE.Vector2} [normalScale] - Normal scale.
 * @property {THREE.Texture} [normalMap] - Normal texture.
 * @property {THREE.Texture} [envMap] - Environment texture.
 * @property {number} [envMapIntensity] - Environment intensity.
 * @property {THREE.Euler} [envMapRotation] - Environment rotation.
 * @property {THREE.Texture} [alphaMap] - Alpha map.

 * These are not supposed to be used directly - they are used by the config system

 * @property {THREE.Vector2} [mapTransformRepeat] - Base color texture transform repeat.
 * @property {THREE.Vector2} [mapTransformOffset] - Base color texture transform ofsset.
 * @property {THREE.Vector2} [mapTransformCenter] - Base color texture transform center.
 * @property {number} [mapTransformRotation] - Base color texture transform rotation.
 *
 * @property {THREE.Vector2} [aoMapTransformRepeat] - Ambient occlusion texture transform repeat.
 * @property {THREE.Vector2} [aoMapTransformOffset] - Ambient occlusion texture transform ofsset.
 * @property {THREE.Vector2} [aoMapTransformCenter] - Ambient occlusion texture transform center.
 * @property {number} [aoMapTransformRotation] - Ambient occlusion texture transform rotation.
 *
 * @property {THREE.Vector2} [armMapTransformRepeat] - Ambient roughness metallic texture transform repeat.
 * @property {THREE.Vector2} [armMapTransformOffset] - Ambient roughness metallic texture transform ofsset.
 * @property {THREE.Vector2} [armMapTransformCenter] - Ambient roughness metallic texture transform center.
 * @property {number} [armMapTransformRotation] - Ambient roughness metallic texture transform rotation.
 *
 * @property {THREE.Vector2} [emissiveMapTransformRepeat] - Emissive texture transform repeat.
 * @property {THREE.Vector2} [emissiveMapTransformOffset] - Emissive texture transform ofsset.
 * @property {THREE.Vector2} [emissiveMapTransformCenter] - Emissive texture transform center.
 * @property {number} [emissiveMapTransformRotation] - Emissive texture transform rotation.
 *
 * @property {THREE.Vector2} [normalMapTransformRepeat] - Normal texture transform repeat.
 * @property {THREE.Vector2} [normalMapTransformOffset] - Normal texture transform ofsset.
 * @property {THREE.Vector2} [normalMapTransformCenter] - Normal texture transform center.
 * @property {number} [normalMapTransformRotation] - Normal texture transform rotation.
 *
 */

export default class PBRMaterial extends ShaderMaterial {
	// eslint-disable-next-line prettier/prettier
	static GENERIC_PROPS = [
		'alphaHash',
		'alphaTest',
		'alphaToCoverage',
		'blendAlpha',
		'blendColor',
		'blendDst',
		'blendDstAlpha',
		'blendEquation',
		'blendEquationAlpha',
		'blendSrc',
		'blendSrcAlpha',
		'blending',
		'colorWrite',
		'defines',
		'depthFunc',
		'depthTest',
		'depthWrite',
		'dithering',
		'forceSinglePass',
		'format',
		'name',
		'opacity',
		'polygonOffset',
		'side',
		'stencilFail',
		'stencilFunc',
		'stencilFuncMask',
		'stencilRef',
		'stencilWrite',
		'stencilWriteMask',
		'stencilZFail',
		'stencilZPass',
		'toneMapped',
		'transparent',
		'userData',
		'vertexColors',
		'visible',
	];
	// eslint-disable-next-line prettier/prettier
	static CONFIG_TYPES_MAPPING = {
		color: Color,
		emissive: Color,
		ambient: Color,
		normalScale: Vector2,
		envMapRotation: Euler,
		mapTransformRepeat: Vector2,
		mapTransformOffset: Vector2,
		mapTransformCenter: Vector2,
		aoMapTransformRepeat: Vector2,
		aoMapTransformOffset: Vector2,
		aoMapTransformCenter: Vector2,
		armMapTransformRepeat: Vector2,
		armMapTransformOffset: Vector2,
		armMapTransformCenter: Vector2,
		emissiveMapTransformRepeat: Vector2,
		emissiveMapTransformOffset: Vector2,
		emissiveMapTransformCenter: Vector2,
		normalMapTransformRepeat: Vector2,
		normalMapTransformOffset: Vector2,
		normalMapTransformCenter: Vector2,
	};
	// eslint-disable-next-line prettier/prettier
	static PROPS_WITH_UV = ['map', 'armMap', 'aoMap', 'emissiveMap', 'normalMap'];

	/** @type {THREE.Color} */
	color;
	/** @type {number} */
	metalness;
	/** @type {number} */
	roughness;
	/** @type {THREE.Texture} */
	map;
	/** @type {number} */
	emissive;
	/** @type {THREE.Texture} */
	emissiveMap;
	/** @type {THREE.Color} */
	ambient;
	/** @type {number} */
	ambientIntensity;
	/** @type {THREE.Texture} */
	armMap;
	/** @type {THREE.Texture} */
	aoMap;
	/** @type {number} */
	metalnessMapIntensity;
	/** @type {number} */
	roughnessMapIntensity;
	/** @type {number} */
	aoMapIntensity;
	/** @type {THREE.Vector2} */
	normalScale;
	/** @type {THREE.Texture} */
	normalMap;
	/** @type {THREE.Texture} */
	envMap;
	/** @type {number} */
	envMapIntensity;
	/** @type {THREE.Euler} */
	envMapRotation;
	/** @type {THREE.Texture} */
	alphaMap;

	/**
	 *
	 * @param {PBRParams & THREE.ShaderMaterialParameters} [params]
	 * @param {THREE.Mesh | THREE.InstancedMesh} caller
	 */
	constructor(
		params = {},
		caller = null,
		defaults = {},
		configTypesMapping = PBRMaterial.CONFIG_TYPES_MAPPING,
		propsWithUV = PBRMaterial.PROPS_WITH_UV,
	) {
		super({ ...PBRMaterial.#filterProps(params, PBRMaterial.GENERIC_PROPS) });

		this.configTypesMapping = configTypesMapping;
		this.propsWithUV = propsWithUV;
		this.#parseParams(params);

		this.isPBRMaterial = true;
		this.type = 'PBRMaterial';
		this.side = FrontSide;

		const defaultsParams = {
			color: new Color(0xffffff),

			metalness: 0,
			roughness: 1,

			map: null,

			emissive: new Color(0x000000),
			emissiveMap: null,

			ambient: new Color(0x55aaff),
			ambientIntensity: 0.8,

			armMap: null,
			aoMap: null,

			metalnessMapIntensity: 1,
			roughnessMapIntensity: 1,
			aoMapIntensity: 1,

			normalScale: new Vector2(1, 1),
			normalMap: null,

			envMap: lisa.scene.environment,
			envMapIntensity: 1,
			envMapRotation: new Euler(0, 0, 0),

			alphaMap: null,

			alphaTest: 0,
			opacity: 1,

			...defaults,
		};

		this.params = params;
		this.params.envMapIntensity = 1;
		this.params.envMapRotation.y = 0.7 * Math.PI;
		this.setDefaults(defaultsParams);
		this.applyParams(PBRMaterial.#filterProps(params, Object.keys(defaultsParams)));

		this.uniforms.tLightMap = { value: null };
		this.uniforms.uLightMapIntensity = { value: 0.8 };

		this.armAsAo = params.armMap?.isTexture && params.aoMap?.isTexture && params.armMap === params.aoMap;

		vs.use(this);
		fs.use(this);

		lisa.state.on(EVENTS.ATTACH, this.#onAttach);

		// app.debug?.mapping.add(this, 'PBR', 1, this.name || params.name || this.type, { caller });
	}

	#onAttach = () => {
		this.uniforms.tLightMap.value = lisa.scene.lisa.atlas.get('lightmapMap');
	};

	set armAsAo(value) {
		if (value) this.defines['SIXTY_ARM_AS_AO'] = 1;
		else delete this.defines['SIXTY_ARM_AS_AO'];

		this.needsUpdate = true;
	}

	get armAsAo() {
		return Object.hasOwn(this.defines, 'SIXTY_ARM_AS_AO');
	}

	setDefaults(defaults) {
		for (const key in defaults) {
			const value = defaults[key];

			const needsUV = this.propsWithUV.includes(key);

			if (key === 'envMapRotation') {
				// Handle specific envMapRotation case (Euler to Matrix3 live conversion)
				this._envMapRotation = new Matrix3();
				setShaderProp(this, key, this._envMapRotation);
				const self = this;
				monitorVector(value, () =>
					self.uniforms.uEnvMapRotation.value.setFromMatrix4(_m4.makeRotationFromEuler(value)),
				);
			} else {
				bindUniform(this, key, needsUV);
			}

			this[key] = value;
		}
	}

	applyParams(params) {
		if (params === undefined) return;

		for (const key in params) {
			const newValue = params[key];

			if (newValue === undefined) {
				console.warn(`Material: parameter '${key}' has value of undefined.`);
				continue;
			}

			const currentValue = this[key];

			if (currentValue === undefined) {
				console.warn(`Material: '${key}' is not a property of ${this.type}.`);
				continue;
			}

			if (currentValue?.isColor && newValue?.isColor) {
				currentValue.set(newValue);
			} else if (
				(currentValue?.isVector3 && newValue?.isVector3) ||
				(currentValue?.isVector2 && newValue?.isVector2) ||
				(currentValue?.isEuler && newValue?.isEuler)
			) {
				currentValue.copy(newValue);
			} else if (currentValue !== undefined && newValue != undefined) {
				this[key] = newValue;
			}
		}
	}

	#parseParams(obj) {
		parseConfig(obj, this.configTypesMapping);

		obj.armMap = obj.armMap || obj.metalnessMap || obj.roughnessMap || null;
		delete obj.metalnessMap;
		delete obj.roughnessMap;
	}

	static #filterProps(params, allowedKeys) {
		return Object.keys(params)
			.filter((/** @type {keyof THREE.MaterialParameters} */ key) => allowedKeys.includes(key))
			.reduce((obj, key) => {
				obj[key] = params[key];
				return obj;
			}, {});
	}
}
